import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { SITE_ORIGIN, SITE_NAME, DEFAULT_OG_IMAGE, SOCIAL_PROFILES, BUSINESS } from './config';
import { mirrorPath } from '../routes/paths';

const SEO = ({ title, description, image, type, breadcrumbs = [], schema = [] }) => {
  const { pathname } = useLocation();
  const isFr = pathname.startsWith('/fr');

  // Build localized alternates via route mapping
  const enPath = mirrorPath(pathname, 'en');
  const frPath = mirrorPath(pathname, 'fr');

  const enUrl = `${SITE_ORIGIN}${enPath}`;
  const frUrl = `${SITE_ORIGIN}${frPath}`;
  const canonical = `${SITE_ORIGIN}${pathname}`;
  const isHome = pathname === '/' || pathname === '/fr';
  const ogType = type || (isHome ? 'website' : 'article');
  const ogLocale = isFr ? 'fr_FR' : 'en_GB';
  const ogPaths = (() => {
    const result = { primary: undefined, secondaryWebp: undefined };
    const orig = image || DEFAULT_OG_IMAGE;
    if (typeof orig === 'string' && /\.webp$/i.test(orig)) {
      // Prefer JPG as primary, keep original WEBP as secondary
      const jpg = orig.replace(/\.webp$/i, '.jpg');
      result.primary = jpg.startsWith('http') ? jpg : `${SITE_ORIGIN}${jpg}`;
      result.secondaryWebp = orig.startsWith('http') ? orig : `${SITE_ORIGIN}${orig}`;
    } else {
      const chosen = orig;
      result.primary = chosen.startsWith('http') ? chosen : `${SITE_ORIGIN}${chosen}`;
    }
    return result;
  })();
  const logoUrl = `${SITE_ORIGIN}/images/Monogramme LL2 Bordeaux.webp`;

  // Build BreadcrumbList JSON-LD
  const crumbDefaults = () => {
    const items = [];
    items.push({ name: isFr ? 'Accueil' : 'Home', path: isFr ? '/fr' : '/' });
    const map = isFr
      ? { '/photographe-mariage': 'Expérience', '/portfolio': 'Portfolio', '/a-propos': 'À propos', '/videaste-mariage': 'Vidéaste', '/contact': 'Contact' }
      : { '/wedding-photographer': 'Experience', '/portfolio': 'Portfolio', '/about': 'About', '/wedding-videographer': 'Videographer', '/contact': 'Contact' };
    const parts = pathname.replace(/^\/fr/, '') || '/';
    const segs = parts.split('/').filter(Boolean);
    if (segs.length >= 1) {
      const first = `/${segs[0]}`;
      if (map[first]) {
        items.push({ name: map[first], path: `${isFr ? '/fr' : ''}${first}` });
      }
    }
    return items;
  };

  const crumbItems = (breadcrumbs && breadcrumbs.length)
    ? [{ name: isFr ? 'Accueil' : 'Home', path: isFr ? '/fr' : '/' }, ...breadcrumbs]
    : crumbDefaults();
  const breadcrumbList = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: crumbItems.map((c, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: c.name,
      item: `${SITE_ORIGIN}${c.path}`,
    })),
  };

  const websiteLD = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    url: SITE_ORIGIN,
    inLanguage: isFr ? 'fr-FR' : 'en-GB',
    alternateName: 'Lino Ludovic Wedding Photographer',
    sameAs: SOCIAL_PROFILES,
    logo: {
      '@type': 'ImageObject',
      url: logoUrl
    }
  };

  const personLD = {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: 'Lino Ludovic',
    url: SITE_ORIGIN,
    image: `${SITE_ORIGIN}${DEFAULT_OG_IMAGE}`,
    jobTitle: 'Wedding Photographer',
    knowsLanguage: ['fr', 'en'],
    sameAs: SOCIAL_PROFILES,
  };

  const proServiceLD = {
    '@context': 'https://schema.org',
    '@type': 'ProfessionalService',
    '@id': `${SITE_ORIGIN}#business`,
    name: BUSINESS.name || SITE_NAME,
    url: SITE_ORIGIN,
    image: logoUrl,
    telephone: BUSINESS.telephone,
    email: BUSINESS.email,
    address: {
      '@type': 'PostalAddress',
      streetAddress: BUSINESS.address.streetAddress,
      addressLocality: BUSINESS.address.addressLocality,
      postalCode: BUSINESS.address.postalCode,
      addressCountry: BUSINESS.address.addressCountry,
    },
    areaServed: BUSINESS.areaServed,
    sameAs: SOCIAL_PROFILES,
  };

  const ldArray = [websiteLD, personLD, proServiceLD, breadcrumbList, ...(Array.isArray(schema) ? schema : [schema]).filter(Boolean)];

  return (
    <Helmet>
      {title && <title>{title}</title>}
      {description && <meta name="description" content={description} />}

      {/* Canonical */}
      <link rel="canonical" href={canonical} />

      {/* Hreflang alternates */}
      <link rel="alternate" href={enUrl} hrefLang="en-gb" />
      <link rel="alternate" href={enUrl} hrefLang="en-us" />
      <link rel="alternate" href={frUrl} hrefLang="fr-fr" />
      <link rel="alternate" href={enUrl} hrefLang="x-default" />

      {/* Open Graph */}
      {title && <meta property="og:title" content={title} />}
      {description && <meta property="og:description" content={description} />}
      <meta property="og:url" content={canonical} />
      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content={ogLocale} />
      <meta property="og:locale:alternate" content={isFr ? 'en_GB' : 'fr_FR'} />
      <meta property="og:image" content={ogPaths.primary} />
      {ogPaths.secondaryWebp && <meta property="og:image" content={ogPaths.secondaryWebp} />}
      {title && <meta property="og:image:alt" content={title} />}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      {title && <meta name="twitter:title" content={title} />}
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={ogPaths.primary} />

      {/* JSON-LD */}
      <script type="application/ld+json">{JSON.stringify(ldArray)}</script>
    </Helmet>
  );
};

export default SEO;
